import {
  CxpFilterGroup,
  CxpFilterOption,
  CxpTask,
  CxpTaskFilterSelection,
  CXP_TASK_FILTER_KEYS,
  NO_SERVICE_LINE_FILTER_ID,
} from '../../models';

/**
 * Applies the sidebar selection (service-line + account checkboxes) and an
 * optional free-text search query to a task list and returns the matching
 * subset. Pure function — no DI, no state lookups; both the data and the
 * inputs are passed in.
 *
 * Search matches case-insensitively against `task.name` and
 * `task.description`. Empty / whitespace-only queries are treated as no
 * search.
 *
 * Used by the facade so containers can compose `applyTaskFilters` with the
 * facade's enriched task signal in a single line.
 */
export function applyTaskFilters(
  tasks: CxpTask[],
  filters: CxpTaskFilterSelection,
  searchQuery?: string,
): CxpTask[] {
  const serviceSet = filters[CXP_TASK_FILTER_KEYS.SERVICE];
  const accountSet = filters[CXP_TASK_FILTER_KEYS.ACCOUNT];
  const normalizedQuery = searchQuery?.trim().toLowerCase() ?? '';

  // Fast path: no filters and no search → return original reference so
  // downstream memoization / OnPush change detection sees identity equality.
  if (!serviceSet?.size && !accountSet?.size && normalizedQuery.length === 0) {
    return tasks;
  }

  return tasks.filter((task) => {
    if (serviceSet?.size) {
      const matchesNoSL = !task.serviceLine && serviceSet.has(NO_SERVICE_LINE_FILTER_ID);
      const matchesSL = task.serviceLine != null && serviceSet.has(task.serviceLine.id);
      if (!matchesNoSL && !matchesSL) return false;
    }
    if (accountSet?.size && !accountSet.has(task.clientId)) {
      return false;
    }
    if (normalizedQuery.length > 0 && !taskMatchesQuery(task, normalizedQuery)) {
      return false;
    }
    return true;
  });
}

function taskMatchesQuery(task: CxpTask, normalizedQuery: string): boolean {
  return (
    task.name.toLowerCase().includes(normalizedQuery) ||
    task.description.toLowerCase().includes(normalizedQuery)
  );
}

/**
 * Builds the sidebar filter groups (service-line + account) with per-option
 * counts. Counts reflect ONLY the tasks passed in — callers are responsible
 * for any pre-filtering (e.g. by tab) so the chip totals match the scope
 * the user expects.
 *
 * Search query and chip selection are intentionally NOT applied here:
 * narrowing the visible list with those should not collapse the chip set.
 *
 * Sorted alphabetically by label.
 */
export function buildFilterGroups(tasks: CxpTask[]): CxpFilterGroup[] {
  const serviceCounts = new Map<number, { name: string; count: number }>();
  const clientCounts = new Map<number, { name: string; count: number }>();

  for (const task of tasks) {
    if (task.serviceLine) {
      const existing = serviceCounts.get(task.serviceLine.id);
      if (existing) {
        existing.count++;
      } else {
        serviceCounts.set(task.serviceLine.id, { name: task.serviceLine.name, count: 1 });
      }
    } else {
      const existing = serviceCounts.get(NO_SERVICE_LINE_FILTER_ID);
      if (existing) {
        existing.count++;
      } else {
        serviceCounts.set(NO_SERVICE_LINE_FILTER_ID, { name: 'No Service Line', count: 1 });
      }
    }
    if (task.clientName !== null) {
      const existing = clientCounts.get(task.clientId);
      if (existing) {
        existing.count++;
      } else {
        clientCounts.set(task.clientId, { name: task.clientName, count: 1 });
      }
    }
  }

  return [
    {
      key: CXP_TASK_FILTER_KEYS.SERVICE,
      label: 'Services',
      options: toSortedOptions(serviceCounts),
    },
    {
      key: CXP_TASK_FILTER_KEYS.ACCOUNT,
      label: 'Accounts',
      options: toSortedOptions(clientCounts),
    },
  ];
}

function toSortedOptions(
  entries: Map<number, { name: string; count: number }>,
): CxpFilterOption[] {
  return [...entries.entries()]
    .map(([id, { name, count }]) => ({ id, label: name, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
